'use client'

import Link from 'next/link'
import { Shirt, Ruler, ShoppingBag, Megaphone, RefreshCw, Sparkles, ArrowRight, DoorOpen, Bot, Plug } from 'lucide-react'

const steps = [
  {
    icon: Shirt,
    title: 'Upload your designs',
    body: 'Apply below and upload your garments — product images, sewing patterns, or 3D assets. Your wardrobe stays yours: review, update, and retire pieces anytime from your retailer dashboard.',
  },
  {
    icon: Ruler,
    title: 'We drape them made-to-measure',
    body: 'Our garment pipeline drafts each piece to the exact body measurements of every avatar, then physically drapes it — real cloth simulation, not a texture swap.',
  },
  {
    icon: ShoppingBag,
    title: 'Users dress their AI assistants',
    body: 'Your pieces appear in the dressing room, worn by each user’s own AI assistant — with a direct path to buy the physical garment from you, ordered through our platform.',
  },
]

const assistantFeatures = [
  {
    icon: DoorOpen,
    title: 'Closet matching',
    body: 'We match a user’s real closet into their avatar’s wardrobe, so the clothes they already own are there to mix with yours.',
  },
  {
    icon: Bot,
    title: 'The assistant tries it on for you',
    body: 'Deciding what to wear? The AI assistant models outfit choices and combinations on the user’s own avatar — their pieces, your pieces, together — before they get dressed.',
  },
  {
    icon: ShoppingBag,
    title: 'Recommends and orders',
    body: 'The assistant recommends new pieces that complete an outfit and can place the order for the user through our system — from “what should I wear” to a purchase, in one conversation.',
  },
]

const partnership = [
  {
    icon: Megaphone,
    title: 'Advertising placements',
    body: 'Sponsored placement in the dressing-room catalog and daily outfit rotations — your brand on the assistant people look at every day.',
  },
  {
    icon: Sparkles,
    title: 'Native wardrobe listings',
    body: 'Your garments listed alongside the platform wardrobe and users’ own closets, categorized, searchable, and fitted to each avatar that tries them on.',
  },
  {
    icon: RefreshCw,
    title: 'Always current',
    body: 'A self-serve retailer wardrobe: upload new drops, update seasonal lines, and retire sold-out pieces the moment your catalog changes.',
  },
  {
    icon: Plug,
    title: 'Store API',
    body: 'Our platform already ships a store API for retailers — orders, agreements, and webhooks — so purchases placed by assistants land directly in your systems.',
    href: '/vendor',
    linkLabel: 'See the vendor platform',
  },
]

export default function ApparelPage() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-emerald-950">
      <nav className="fixed top-0 left-0 right-0 z-50 bg-slate-950/80 backdrop-blur-xl border-b border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link href="/" className="text-white font-bold text-lg">
              Link<span className="text-emerald-400">Box</span> AI
            </Link>
            <div className="hidden md:flex items-center gap-8">
              <Link href="/features" className="text-gray-400 hover:text-white transition-colors text-sm font-medium">Features</Link>
              <Link href="/vendor" className="text-gray-400 hover:text-white transition-colors text-sm font-medium">Vendor</Link>
              <Link href="/apparel" className="text-white text-sm font-medium">Apparel</Link>
              <Link href="/vendor/login" className="text-gray-400 hover:text-white transition-colors text-sm font-medium">Vendor Sign In</Link>
            </div>
            <Link
              href="/apparel/apply"
              className="rounded-lg bg-emerald-500 px-4 py-2 text-sm font-semibold text-slate-950 hover:bg-emerald-400 transition-colors"
            >
              Become a partner
            </Link>
          </div>
        </div>
      </nav>

      <section className="relative pt-36 pb-24 overflow-hidden">
        <div className="absolute top-16 left-1/4 w-96 h-96 bg-gradient-to-br from-emerald-500/10 to-cyan-500/10 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-gradient-to-br from-pink-500/10 to-emerald-500/10 rounded-full blur-3xl" />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <span className="inline-block rounded-full border border-emerald-500/40 bg-emerald-500/10 px-4 py-1 text-sm text-emerald-300 mb-6">
            For specialty apparel retailers
          </span>
          <h1 className="text-5xl md:text-7xl font-bold text-white leading-tight">
            Dress their
            <span className="bg-gradient-to-r from-emerald-400 to-cyan-400 bg-clip-text text-transparent"> AI assistant</span>.
            <br />
            Win their wardrobe.
          </h1>
          <p className="mt-6 text-lg text-gray-400 max-w-2xl mx-auto">
            Every LinkBox user has a personal AI assistant with an avatar, a
            dressing room, and a wardrobe. Upload your designs and let users
            dress their assistants in your line — draped made-to-measure on
            every body, one order away from their real closet.
          </p>
          <div className="mt-10 flex items-center justify-center gap-4">
            <Link
              href="/apparel/apply"
              className="inline-flex items-center gap-2 rounded-lg bg-emerald-500 px-6 py-3 font-semibold text-slate-950 hover:bg-emerald-400 transition-colors"
            >
              Apply for partnership <ArrowRight className="h-4 w-4" />
            </Link>
            <Link
              href="#how-it-works"
              className="rounded-lg border border-gray-700 px-6 py-3 font-semibold text-gray-300 hover:text-white hover:border-gray-500 transition-colors"
            >
              How it works
            </Link>
          </div>
        </div>
      </section>

      <section id="how-it-works" className="py-24 bg-slate-900/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-14">How it works</h2>
          <div className="grid md:grid-cols-3 gap-8">
            {steps.map((s, i) => (
              <div key={s.title} className="rounded-2xl border border-gray-800 bg-slate-950/60 p-8">
                <div className="flex items-center gap-3 mb-4">
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-500/15">
                    <s.icon className="h-5 w-5 text-emerald-400" />
                  </div>
                  <span className="text-sm text-gray-500">Step {i + 1}</span>
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">{s.title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-4">
            Your customer&apos;s new mirror
          </h2>
          <p className="text-gray-400 text-center max-w-2xl mx-auto mb-14">
            The assistant doesn&apos;t just wear clothes — it helps people decide
            what <em>they</em> wear, and your line is in the room when they decide.
          </p>
          <div className="grid md:grid-cols-3 gap-8">
            {assistantFeatures.map(f => (
              <div key={f.title} className="rounded-2xl border border-gray-800 bg-gradient-to-br from-slate-900 to-slate-950 p-8">
                <f.icon className="h-6 w-6 text-pink-400 mb-4" />
                <h3 className="text-lg font-semibold text-white mb-2">{f.title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{f.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 bg-slate-900/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-4">Partnership</h2>
          <p className="text-gray-400 text-center max-w-2xl mx-auto mb-14">
            Advertising reach plus a commerce channel, in the most personal
            placement there is: what someone&apos;s own assistant wears — and
            recommends.
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {partnership.map(p => (
              <div key={p.title} className="rounded-2xl border border-gray-800 bg-gradient-to-br from-slate-900 to-slate-950 p-8">
                <p.icon className="h-6 w-6 text-cyan-400 mb-4" />
                <h3 className="text-lg font-semibold text-white mb-2">{p.title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{p.body}</p>
                {p.href && (
                  <Link href={p.href} className="mt-3 inline-flex items-center gap-1 text-sm text-cyan-400 hover:text-cyan-300">
                    {p.linkLabel} <ArrowRight className="h-3.5 w-3.5" />
                  </Link>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Put your line on their assistants
          </h2>
          <p className="text-gray-400 mb-8">
            Tell us about your brand. We&apos;ll follow up with partnership and
            advertising options and get your first pieces draped.
          </p>
          <Link
            href="/apparel/apply"
            className="inline-flex items-center gap-2 rounded-lg bg-emerald-500 px-8 py-3 font-semibold text-slate-950 hover:bg-emerald-400 transition-colors"
          >
            Apply for partnership <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>

      <footer className="border-t border-gray-800 py-10 text-center text-sm text-gray-500">
        <Link href="/" className="hover:text-white transition-colors">← Back to LinkBox AI</Link>
      </footer>
    </div>
  )
}
